/* Copyright (c) 2020-present CloudIO Inc */

import { Item } from '../../registry/Component.type';
import { pageAtom } from 'designer/atoms';
import { genId } from 'designer/state';
import {
  AnalyzeContext,
  AnalyzeIssue,
  AnalyzeResult,
} from 'layout/Sidebar/panels/TreeView/AnalyzeUtils';

export async function analyzeFolder({
  platform,
  appUid,
  pageId,
  itemId,
  mgr,
}: AnalyzeContext): Promise<AnalyzeResult> {
  const alerts: AnalyzeIssue[] = [];
  const autoFixes: (() => void)[] = [];
  const page = platform.getPage(appUid, pageId);
  if (!page) {
    return null;
  }
  const item = page[itemId];
  if (
    item.children.filter((id) => page[id]?.type === 'DataStore').length > 2 &&
    item.children.filter(
      (id) =>
        page[id]?.type === 'ReadOnlyDataStore' ||
        page[id]?.type === 'LocalDataStore' ||
        page[id]?.type === 'TransientDataStore',
    ).length > 2
  ) {
    const fix = () => {
      const latestPage = platform.getPage(appUid, pageId);
      if (!latestPage) {
        return;
      }
      const newPage = { ...latestPage };
      const folderItem = { ...newPage[itemId] };
      let children = folderItem.children.filter(
        (id) => newPage[id]?.type === 'DataStore',
      );

      const editableFolder: Item<'Folder'> = {
        parent: itemId,
        type: 'Folder',
        itemId: genId(newPage),
        props: { name: 'Editable' },
        children,
      };
      newPage[editableFolder.itemId] = editableFolder;

      children.forEach((id) => {
        newPage[id] = { ...newPage[id], parent: editableFolder.itemId };
      });

      children = folderItem.children.filter(
        (id) =>
          newPage[id]?.type === 'ReadOnlyDataStore' ||
          newPage[id]?.type === 'LocalDataStore' ||
          newPage[id]?.type === 'TransientDataStore',
      );

      const roFolder: Item<'Folder'> = {
        parent: itemId,
        type: 'Folder',
        itemId: genId(newPage),
        props: { name: 'Readonly' },
        children,
      };
      newPage[roFolder.itemId] = roFolder;

      children.forEach((id) => {
        newPage[id] = { ...newPage[id], parent: roFolder.itemId };
      });

      folderItem.children = [
        editableFolder.itemId,
        roFolder.itemId,
        ...folderItem.children.filter(
          (id) =>
            !(
              newPage[id]?.type === 'DataStore' ||
              newPage[id]?.type === 'ReadOnlyDataStore' ||
              newPage[id]?.type === 'LocalDataStore' ||
              newPage[id]?.type === 'TransientDataStore'
            ),
        ),
      ];

      newPage[itemId] = folderItem;
      platform.setter(pageAtom, newPage, {
        params: { appUid, pageId },
      });
    };
    autoFixes.push(fix);
    alerts.push({
      key: `split-folder-${itemId}`,
      fix,
      alert:
        'Organize editable and readonly Data Stores in different sub Folder',
      itemId,
      severity: 'info',
    });
  }

  return [alerts, autoFixes];
}
